import React from 'react'
import { Sidebar, Button, Heading, Text, LoadingSpinner, Input, Tooltip } from '../../components/ui'
import { SortableTableHeader } from '../../components/ui/SortableTableHeader'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import { admDespesasService } from '../../services/admDespesas'
import { admCadastrosService } from '../../services/admCadastros'
import { logDelete, logUpdate } from '../../services/userLogs'
import { printService } from '../../services/printService'
import { useAuthContext } from '../../contexts/AuthContext'
import type { AdmNfeHeader, AdmNfeItem, AdmNfseHeader, AdmNfseItem } from '../../types/admDespesas'
import type { AdmCategoria, AdmCentroCusto } from '../../types/admCadastros'
import type { PrintConfig, PrintColumn, PrintRow } from '../../types/print'
import { TrashIcon, DocumentIcon, PlusIcon, ArrowUpTrayIcon, XMarkIcon, CheckCircleIcon, PencilIcon, ArrowDownTrayIcon, InformationCircleIcon } from '@heroicons/react/24/outline'

const N8N_WEBHOOK_URL = 'https://webhook.pasiai.com/webhook/d42b04fe-3994-4b4f-a995-285a95824397'

const ADM_STORAGE_BASE = `${process.env.REACT_APP_SUPABASE_URL ?? ''}/storage/v1/object/public/ADM/notas/`

type ActiveTab = 'nfe' | 'nfse'
type ActiveSubTab = 'notes' | 'items'
type PrintScope = 'page' | 'filtered'
type PrintViewKey = 'nfe_notes' | 'nfe_items' | 'nfse_notes' | 'nfse_items'
type HeaderEditType = 'nfe' | 'nfse'
type HeaderEditModalState = {
  type: HeaderEditType
  id: string
  number: string
  series: string
  rpsNumber: string
  name: string
  document: string
  providerIm: string
  municipality: string
  recipientName: string
  recipientDocument: string
  competenceDate: string
  accessKey: string
  issueDate: string
  totalProducts: string
  totalServices: string
  totalFreight: string
  totalDiscount: string
  netValue: string
  totalValue: string
  paymentMethod: string
  fileName: string
}

type ItemEditModalState = {
  tableType: 'nfe' | 'nfse'
  itemId: string
  headerId: string
  headerNumber: string
  description: string
  ncm: string
  unit: string
  serviceCode: string
  quantity: string
  unitValue: string
  totalValue: string
  categoryId: string
  costCenterId: string
}

function formatCurrency(value: number | null): string {
  if (value == null) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatQuantity(value: number): string {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 6 })
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('pt-BR')
}

function getNfseDisplayTotal(nfse: AdmNfseHeader): number | null {
  return nfse.net_value ?? nfse.total_services ?? null
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function formatDocument(value: string | null): string {
  if (!value) return '—'
  const digits = value.replace(/\D/g, '')
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }
  return value
}

const InfoTip: React.FC<{ content: React.ReactNode; position?: 'top' | 'bottom' | 'left' | 'right' }> = ({ content, position = 'bottom' }) => (
  <Tooltip content={content} position={position} constrainWidth className="inline-flex shrink-0 align-middle">
    <span className="inline-flex rounded-full p-0.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
      <InformationCircleIcon className="h-4 w-4" />
    </span>
  </Tooltip>
)

const AdmDespesas: React.FC = () => {
  const { user } = useAuthContext()
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('nfe')
  const [nfeSubTab, setNfeSubTab] = React.useState<ActiveSubTab>('notes')
  const [nfseSubTab, setNfseSubTab] = React.useState<ActiveSubTab>('notes')
  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)

  const [nfeList, setNfeList] = React.useState<AdmNfeHeader[]>([])
  const [nfseList, setNfseList] = React.useState<AdmNfseHeader[]>([])
  const [allNfeItems, setAllNfeItems] = React.useState<AdmNfeItem[]>([])
  const [allNfseItems, setAllNfseItems] = React.useState<AdmNfseItem[]>([])

  const [categoryMap, setCategoryMap] = React.useState<Map<string, string>>(new Map())
  const [costCenterMap, setCostCenterMap] = React.useState<Map<string, string>>(new Map())
  const [categorias, setCategorias] = React.useState<AdmCategoria[]>([])
  const [centrosCusto, setCentrosCusto] = React.useState<AdmCentroCusto[]>([])

  const PAGE_SIZE = 20
  // Paginação
  const [nfeNotesPage, setNfeNotesPage] = React.useState(1)
  const [nfeItemsPage, setNfeItemsPage] = React.useState(1)
  const [nfseNotesPage, setNfseNotesPage] = React.useState(1)
  const [nfseItemsPage, setNfseItemsPage] = React.useState(1)

  // Ordenação
  const [nfeNotesSort, setNfeNotesSort] = React.useState('issue_date')
  const [nfeNotesSortOrder, setNfeNotesSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [nfseNotesSort, setNfseNotesSort] = React.useState('issue_date')
  const [nfseNotesSortOrder, setNfseNotesSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [nfeItemsSort, setNfeItemsSort] = React.useState('nfe_id')
  const [nfeItemsSortOrder, setNfeItemsSortOrder] = React.useState<'asc' | 'desc'>('asc')
  const [nfseItemsSort, setNfseItemsSort] = React.useState('nfse_id')
  const [nfseItemsSortOrder, setNfseItemsSortOrder] = React.useState<'asc' | 'desc'>('asc')

  const toggleSort = (
    current: string, setSort: (v: string) => void,
    currentOrder: 'asc' | 'desc', setOrder: (v: 'asc' | 'desc') => void,
    setPage: (v: number) => void,
  ) => (key: string) => {
    if (current === key) {
      setOrder(currentOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSort(key)
      setOrder('asc')
    }
    setPage(1)
  }

  // Filtros - Notas NF-e
  const [nfeNotesSearch, setNfeNotesSearch] = React.useState('')
  const [nfeNotesCnpj, setNfeNotesCnpj] = React.useState('')
  const [nfeNotesNumber, setNfeNotesNumber] = React.useState('')
  const [nfeNotesAccessKey, setNfeNotesAccessKey] = React.useState('')
  const [nfeNotesFileName, setNfeNotesFileName] = React.useState('')
  const [nfeNotesDateFrom, setNfeNotesDateFrom] = React.useState('')
  const [nfeNotesDateTo, setNfeNotesDateTo] = React.useState('')
  // Filtros - Itens NF-e
  const [nfeItemsSearch, setNfeItemsSearch] = React.useState('')
  const [nfeItemsIssuer, setNfeItemsIssuer] = React.useState('')
  const [nfeItemsNoteNumber, setNfeItemsNoteNumber] = React.useState('')
  const [nfeItemsAccessKey, setNfeItemsAccessKey] = React.useState('')
  const [nfeItemsFileName, setNfeItemsFileName] = React.useState('')
  const [nfeItemsCategory, setNfeItemsCategory] = React.useState('')
  const [nfeItemsCostCenter, setNfeItemsCostCenter] = React.useState('')
  // Filtros - Notas NFS-e
  const [nfseNotesSearch, setNfseNotesSearch] = React.useState('')
  const [nfseNotesCnpj, setNfseNotesCnpj] = React.useState('')
  const [nfseNotesNumber, setNfseNotesNumber] = React.useState('')
  const [nfseNotesAccessKey, setNfseNotesAccessKey] = React.useState('')
  const [nfseNotesFileName, setNfseNotesFileName] = React.useState('')
  const [nfseNotesDateFrom, setNfseNotesDateFrom] = React.useState('')
  const [nfseNotesDateTo, setNfseNotesDateTo] = React.useState('')
  // Filtros - Itens NFS-e
  const [nfseItemsSearch, setNfseItemsSearch] = React.useState('')
  const [nfseItemsProvider, setNfseItemsProvider] = React.useState('')
  const [nfseItemsNoteNumber, setNfseItemsNoteNumber] = React.useState('')
  const [nfseItemsAccessKey, setNfseItemsAccessKey] = React.useState('')
  const [nfseItemsFileName, setNfseItemsFileName] = React.useState('')
  const [nfseItemsServiceCode, setNfseItemsServiceCode] = React.useState('')
  const [nfseItemsCategory, setNfseItemsCategory] = React.useState('')
  const [nfseItemsCostCenter, setNfseItemsCostCenter] = React.useState('')

  type EditingItem = { tableType: 'nfe' | 'nfse'; itemId: string; field: 'category' | 'cost_center'; context: 'modal' | 'list' }
  const [editingItem, setEditingItem] = React.useState<EditingItem | null>(null)
  const [editingValue, setEditingValue] = React.useState<string>('')
  const [savingItem, setSavingItem] = React.useState<string | null>(null)

  const [moduleInfo, setModuleInfo] = React.useState<{ display_name: string; description: string | null } | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{
    type: 'nfe' | 'nfse'
    id: string
    label: string
  } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)
  const [headerEditModal, setHeaderEditModal] = React.useState<HeaderEditModalState | null>(null)
  const [submittingHeaderEdit, setSubmittingHeaderEdit] = React.useState(false)
  const [itemEditModal, setItemEditModal] = React.useState<ItemEditModalState | null>(null)
  const [submittingItemEdit, setSubmittingItemEdit] = React.useState(false)

  type FileStatus = { file: File; status: 'pending' | 'sending' | 'success' | 'error'; error?: string; finishedAt?: Date }

  const [detailNfe, setDetailNfe] = React.useState<{ header: AdmNfeHeader; items: AdmNfeItem[] } | null>(null)
  const [detailNfse, setDetailNfse] = React.useState<{ header: AdmNfseHeader; items: AdmNfseItem[] } | null>(null)
  const [loadingDetail, setLoadingDetail] = React.useState(false)

  const [uploadModalOpen, setUploadModalOpen] = React.useState(false)
  const [uploadFiles, setUploadFiles] = React.useState<FileStatus[]>([])
  const [isProcessing, setIsProcessing] = React.useState(false)
  const [processingDone, setProcessingDone] = React.useState(false)
  const [processingStartedAt, setProcessingStartedAt] = React.useState<Date | null>(null)
  const [processingFinishedAt, setProcessingFinishedAt] = React.useState<Date | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [printDialogView, setPrintDialogView] = React.useState<PrintViewKey | null>(null)

  const { hasPermission: canDelete } = usePermission('adm_despesas', 'delete')
  const { hasPermission: canEdit } = usePermission('adm_despesas', 'update')

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message
    const maybe = error as { message?: string; code?: string }
    if (maybe?.message) return maybe.message
    if (maybe?.code) return `Erro (${maybe.code})`
    return 'Erro inesperado ao processar solicitação.'
  }

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [nfe, nfse, nfeItems, nfseItems, cats, centros] = await Promise.all([
        admDespesasService.listNfe(),
        admDespesasService.listNfse(),
        admDespesasService.listAllNfeItems(),
        admDespesasService.listAllNfseItems(),
        admCadastrosService.listCategorias(),
        admCadastrosService.listCentrosCusto(),
      ])
      setNfeList(nfe)
      setNfseList(nfse)
      setAllNfeItems(nfeItems)
      setAllNfseItems(nfseItems)
      setCategoryMap(new Map(cats.map((c) => [c.id, c.name])))
      setCostCenterMap(new Map(centros.map((c) => [c.id, c.name])))
      setCategorias(cats)
      setCentrosCusto(centros)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName('adm_despesas')
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || 'ADM • Despesas',
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  const summarizeFilters = (entries: Array<[string, string]>): Array<{ label: string; value: string }> => {
    return entries
      .map(([label, value]) => [label, value.trim()] as const)
      .filter(([, value]) => value.length > 0)
      .map(([label, value]) => ({ label, value }))
  }

  const getPrintPayload = (view: PrintViewKey, scope: PrintScope): PrintConfig => {
    const generatedAt = new Date().toLocaleString('pt-BR')
    const scopeLabel = scope === 'page' ? 'Página atual' : 'Todos os filtrados'

    if (view === 'nfe_notes') {
      const source = scope === 'page' ? paginatedNfeNotes : sortedNfeNotes
      const columns: PrintColumn[] = [
        { key: 'number', label: 'Número' },
        { key: 'issuer_name', label: 'Emitente' },
        { key: 'issue_date', label: 'Emissão' },
        { key: 'items_count', label: 'Nº Itens', align: 'right' },
        { key: 'total_value', label: 'Valor Total', align: 'right' },
      ]
      const rows: PrintRow[] = source.map((nfe) => ({
        number: nfe.number || '—',
        issuer_name: nfe.issuer_name || '—',
        issue_date: formatDate(nfe.issue_date),
        items_count: nfeItemsCountByHeader.get(nfe.id) ?? 0,
        total_value: formatCurrency(nfe.total_value),
      }))
      return {
        reportTitle: 'ADM • Despesas • Compras NF-e • Notas Fiscais',
        generatedAt,
        scopeLabel,
        totalRows: rows.length,
        filters: summarizeFilters([
          ['Descrição Produto', nfeItemsSearch],
          ['Emitente', nfeItemsIssuer],
          ['Número da nota', nfeItemsNoteNumber],
          ['Chave de acesso', nfeItemsAccessKey],
          ['Nome do arquivo', nfeItemsFileName],
          ['Categoria', nfeItemsCategory ? (categoryMap.get(nfeItemsCategory) ?? nfeItemsCategory) : ''],
          ['Centro de custo', nfeItemsCostCenter ? (costCenterMap.get(nfeItemsCostCenter) ?? nfeItemsCostCenter) : ''],
        ]),
        columns,
        rows,
      }
    }

    if (view === 'nfe_items') {
      const source = scope === 'page' ? paginatedNfeItems : sortedNfeItems
      const columns: PrintColumn[] = [
        { key: 'note_number', label: 'Nota' },
        { key: 'issuer_name', label: 'Emitente' },
        { key: 'description', label: 'Descrição' },
        { key: 'quantity', label: 'Qtd', align: 'right' },
        { key: 'unit', label: 'Un' },
        { key: 'unit_value', label: 'Vl. Unit.', align: 'right' },
        { key: 'total_value', label: 'Total', align: 'right' },
        { key: 'category', label: 'Categoria' },
        { key: 'cost_center', label: 'Centro de Custo' },
      ]
      const rows: PrintRow[] = source.map((item) => {
        const header = nfeHeaderMap.get(item.nfe_id)
        return {
          note_number: header?.number || '—',
          issuer_name: header?.issuer_name || '—',
          description: item.description || '—',
          quantity: item.quantity ?? '—',
          unit: item.unit || '—',
          unit_value: formatCurrency(item.unit_value),
          total_value: formatCurrency(item.total_value),
          category: item.category_id ? (categoryMap.get(item.category_id) ?? item.category_id) : '—',
          cost_center: item.cost_center_id ? (costCenterMap.get(item.cost_center_id) ?? item.cost_center_id) : '—',
        }
      })
      return {
        reportTitle: 'ADM • Despesas • Compras NF-e • Itens',
        generatedAt,
        scopeLabel,
        totalRows: rows.length,
        filters: summarizeFilters([
          ['Descrição Produto', nfeItemsSearch],
          ['Emitente', nfeItemsIssuer],
          ['Nº da nota', nfeItemsNoteNumber],
          ['Chave de acesso', nfeItemsAccessKey],
          ['Nome do arquivo', nfeItemsFileName],
          ['Categoria', nfeItemsCategory ? (categoryMap.get(nfeItemsCategory) ?? nfeItemsCategory) : ''],
          ['Centro de custo', nfeItemsCostCenter ? (costCenterMap.get(nfeItemsCostCenter) ?? nfeItemsCostCenter) : ''],
        ]),
        columns,
        rows,
      }
    }

    if (view === 'nfse_notes') {
      const source = scope === 'page' ? paginatedNfseNotes : sortedNfseNotes
      const columns: PrintColumn[] = [
        { key: 'number', label: 'Número' },
        { key: 'provider_name', label: 'Prestador' },
        { key: 'competence', label: 'Competência' },
        { key: 'total_value', label: 'Valor Total', align: 'right' },
      ]
      const rows: PrintRow[] = source.map((nfse) => ({
        number: nfse.number || '—',
        provider_name: nfse.provider_name || '—',
        competence: nfse.competence_date
          ? new Date(`${nfse.competence_date}T00:00:00`).toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })
          : '—',
        total_value: formatCurrency(getNfseDisplayTotal(nfse)),
      }))
      return {
        reportTitle: 'ADM • Despesas • Serviços NFS-e • Notas Fiscais',
        generatedAt,
        scopeLabel,
        totalRows: rows.length,
        filters: summarizeFilters([
          ['Prestador', nfseItemsProvider],
          ['Número da nota', nfseItemsNoteNumber],
          ['Chave de acesso', nfseItemsAccessKey],
          ['Nome do arquivo', nfseItemsFileName],
          ['Categoria', nfseItemsCategory ? (categoryMap.get(nfseItemsCategory) ?? nfseItemsCategory) : ''],
          ['Centro de custo', nfseItemsCostCenter ? (costCenterMap.get(nfseItemsCostCenter) ?? nfseItemsCostCenter) : ''],
        ]),
        columns,
        rows,
      }
    }

    const source = scope === 'page' ? paginatedNfseItems : sortedNfseItems
    const columns: PrintColumn[] = [
      { key: 'note_number', label: 'Nota' },
      { key: 'provider_name', label: 'Prestador' },
      { key: 'description', label: 'Detalhes do Serviço' },
      { key: 'quantity', label: 'Qtd', align: 'right' },
      { key: 'unit_value', label: 'Vl. Unit.', align: 'right' },
      { key: 'total_value', label: 'Total', align: 'right' },
      { key: 'category', label: 'Categoria' },
      { key: 'cost_center', label: 'Centro de Custo' },
    ]
    const rows: PrintRow[] = source.map((item) => {
      const header = nfseHeaderMap.get(item.nfse_id)
      return {
        note_number: header?.number || '—',
        provider_name: header?.provider_name || '—',
        description: item.description || '—',
        quantity: item.quantity ?? '—',
        unit_value: formatCurrency(item.unit_value),
        total_value: formatCurrency(item.total_value),
        category: item.category_id ? (categoryMap.get(item.category_id) ?? item.category_id) : '—',
        cost_center: item.cost_center_id ? (costCenterMap.get(item.cost_center_id) ?? item.cost_center_id) : '—',
      }
    })
    return {
      reportTitle: 'ADM • Despesas • Serviços NFS-e • Serviços',
      generatedAt,
      scopeLabel,
      totalRows: rows.length,
      filters: summarizeFilters([
        ['Descrição Produto', nfseItemsSearch],
        ['Prestador', nfseItemsProvider],
        ['Nº da nota', nfseItemsNoteNumber],
        ['Chave de acesso', nfseItemsAccessKey],
        ['Nome do arquivo', nfseItemsFileName],
        ['Categoria', nfseItemsCategory ? (categoryMap.get(nfseItemsCategory) ?? nfseItemsCategory) : ''],
        ['Centro de custo', nfseItemsCostCenter ? (costCenterMap.get(nfseItemsCostCenter) ?? nfseItemsCostCenter) : ''],
      ]),
      columns,
      rows,
    }
  }

  const openPrintDialog = (view: PrintViewKey) => {
    setPrintDialogView(view)
  }

  const handlePrint = (scope: PrintScope) => {
    if (!printDialogView) return
    const payload = getPrintPayload(printDialogView, scope)
    printService.print(payload)
    setPrintDialogView(null)
  }

  const openDeleteModal = (type: 'nfe' | 'nfse', id: string, label: string) => {
    setDeleteModal({ type, id, label })
    setDeleteReason('')
  }

  const closeDeleteModal = () => {
    setDeleteModal(null)
    setDeleteReason('')
  }

  const openHeaderEditModal = (type: HeaderEditType, header: AdmNfeHeader | AdmNfseHeader) => {
    if (type === 'nfe') {
      const nfe = header as AdmNfeHeader
      setHeaderEditModal({
        type,
        id: nfe.id,
        number: nfe.number ?? '',
        series: nfe.series ?? '',
        rpsNumber: '',
        name: nfe.issuer_name ?? '',
        document: nfe.issuer_cnpj ?? '',
        providerIm: '',
        municipality: '',
        recipientName: nfe.recipient_name ?? '',
        recipientDocument: nfe.recipient_document ?? '',
        competenceDate: '',
        accessKey: nfe.access_key ?? '',
        issueDate: nfe.issue_date ? nfe.issue_date.slice(0, 10) : '',
        totalProducts: nfe.total_products != null ? String(nfe.total_products) : '',
        totalServices: '',
        totalFreight: nfe.total_freight != null ? String(nfe.total_freight) : '',
        totalDiscount: nfe.total_discount != null ? String(nfe.total_discount) : '',
        netValue: '',
        totalValue: nfe.total_value != null ? String(nfe.total_value) : '',
        paymentMethod: nfe.payment_method ?? '',
        fileName: nfe.file_name ?? '',
      })
      return
    }

    const nfse = header as AdmNfseHeader
    setHeaderEditModal({
      type,
      id: nfse.id,
      number: nfse.number ?? '',
      series: '',
      rpsNumber: nfse.rps_number ?? '',
      name: nfse.provider_name ?? '',
      document: nfse.provider_cnpj ?? '',
      providerIm: nfse.provider_im ?? '',
      municipality: nfse.municipality ?? '',
      recipientName: '',
      recipientDocument: '',
      competenceDate: nfse.competence_date ? nfse.competence_date.slice(0, 10) : '',
      accessKey: nfse.access_key ?? '',
      issueDate: nfse.issue_date ? nfse.issue_date.slice(0, 10) : '',
      totalProducts: '',
      totalServices: nfse.total_services != null ? String(nfse.total_services) : '',
      totalFreight: '',
      totalDiscount: '',
      netValue: nfse.net_value != null ? String(nfse.net_value) : '',
      totalValue: '',
      paymentMethod: nfse.payment_method ?? '',
      fileName: nfse.file_name ?? '',
    })
  }

  const closeHeaderEditModal = () => {
    if (submittingHeaderEdit) return
    setHeaderEditModal(null)
  }

  const handleSaveHeaderEdit = async () => {
    if (!headerEditModal) return
    setSubmittingHeaderEdit(true)
    try {
      if (headerEditModal.type === 'nfe') {
        const currentHeader = nfeList.find((item) => item.id === headerEditModal.id)
        const patch = {
          number: headerEditModal.number.trim() || null,
          series: headerEditModal.series.trim() || null,
          issuer_name: headerEditModal.name.trim() || null,
          issuer_cnpj: headerEditModal.document.trim() || null,
          recipient_name: headerEditModal.recipientName.trim() || null,
          recipient_document: headerEditModal.recipientDocument.trim() || null,
          access_key: headerEditModal.accessKey.trim() || currentHeader?.access_key || '',
          issue_date: headerEditModal.issueDate || null,
          total_products: headerEditModal.totalProducts.trim() ? Number(headerEditModal.totalProducts) : null,
          total_freight: headerEditModal.totalFreight.trim() ? Number(headerEditModal.totalFreight) : null,
          total_discount: headerEditModal.totalDiscount.trim() ? Number(headerEditModal.totalDiscount) : null,
          total_value: headerEditModal.totalValue.trim() ? Number(headerEditModal.totalValue) : null,
          payment_method: headerEditModal.paymentMethod.trim() || null,
          file_name: headerEditModal.fileName.trim() || null,
        }
        await admDespesasService.updateNfeHeader(headerEditModal.id, patch)
        setNfeList((prev) => prev.map((item) => (item.id === headerEditModal.id ? { ...item, ...patch } : item)))
        setDetailNfe((prev) => prev && prev.header.id === headerEditModal.id ? { ...prev, header: { ...prev.header, ...patch } } : prev)
        await logUpdate(
          'other',
          headerEditModal.id,
          `Atualizou cabeçalho da NF-e ${patch.number ?? currentHeader?.number ?? headerEditModal.id}`,
          {
            number: currentHeader?.number ?? null,
            series: currentHeader?.series ?? null,
            issuer_name: currentHeader?.issuer_name ?? null,
            issuer_cnpj: currentHeader?.issuer_cnpj ?? null,
            recipient_name: currentHeader?.recipient_name ?? null,
            recipient_document: currentHeader?.recipient_document ?? null,
            access_key: currentHeader?.access_key ?? null,
            issue_date: currentHeader?.issue_date ?? null,
            total_products: currentHeader?.total_products ?? null,
            total_freight: currentHeader?.total_freight ?? null,
            total_discount: currentHeader?.total_discount ?? null,
            total_value: currentHeader?.total_value ?? null,
            payment_method: currentHeader?.payment_method ?? null,
            file_name: currentHeader?.file_name ?? null,
          },
          patch,
          { additional_info: 'Módulo ADM • Despesas (cabeçalho NF-e)' }
        )
      } else {
        const currentHeader = nfseList.find((item) => item.id === headerEditModal.id)
        const patch = {
          number: headerEditModal.number.trim() || null,
          rps_number: headerEditModal.rpsNumber.trim() || null,
          provider_name: headerEditModal.name.trim() || null,
          provider_cnpj: headerEditModal.document.trim() || null,
          provider_im: headerEditModal.providerIm.trim() || null,
          municipality: headerEditModal.municipality.trim() || null,
          customer_name: headerEditModal.recipientName.trim() || null,
          customer_document: headerEditModal.recipientDocument.trim() || null,
          access_key: headerEditModal.accessKey.trim() || currentHeader?.access_key || '',
          issue_date: headerEditModal.issueDate || null,
          competence_date: headerEditModal.competenceDate || null,
          total_services: headerEditModal.totalServices.trim() ? Number(headerEditModal.totalServices) : null,
          total_discount: headerEditModal.totalDiscount.trim() ? Number(headerEditModal.totalDiscount) : null,
          net_value: headerEditModal.netValue.trim() ? Number(headerEditModal.netValue) : null,
          payment_method: headerEditModal.paymentMethod.trim() || null,
          file_name: headerEditModal.fileName.trim() || null,
        }
        await admDespesasService.updateNfseHeader(headerEditModal.id, patch)
        setNfseList((prev) => prev.map((item) => (item.id === headerEditModal.id ? { ...item, ...patch } : item)))
        setDetailNfse((prev) => prev && prev.header.id === headerEditModal.id ? { ...prev, header: { ...prev.header, ...patch } } : prev)
        await logUpdate(
          'other',
          headerEditModal.id,
          `Atualizou cabeçalho da NFS-e ${patch.number ?? currentHeader?.number ?? headerEditModal.id}`,
          {
            number: currentHeader?.number ?? null,
            rps_number: currentHeader?.rps_number ?? null,
            provider_name: currentHeader?.provider_name ?? null,
            provider_cnpj: currentHeader?.provider_cnpj ?? null,
            provider_im: currentHeader?.provider_im ?? null,
            municipality: currentHeader?.municipality ?? null,
            customer_name: currentHeader?.customer_name ?? null,
            customer_document: currentHeader?.customer_document ?? null,
            access_key: currentHeader?.access_key ?? null,
            issue_date: currentHeader?.issue_date ?? null,
            competence_date: currentHeader?.competence_date ?? null,
            total_services: currentHeader?.total_services ?? null,
            total_discount: currentHeader?.total_discount ?? null,
            net_value: currentHeader?.net_value ?? null,
            payment_method: currentHeader?.payment_method ?? null,
            file_name: currentHeader?.file_name ?? null,
          },
          patch,
          { additional_info: 'Módulo ADM • Despesas (cabeçalho NFS-e)' }
        )
      }
      setHeaderEditModal(null)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingHeaderEdit(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteModal || !user?.id) return
    if (!deleteReason.trim()) return

    setSubmittingDelete(true)
    try {
      if (deleteModal.type === 'nfe') {
        await admDespesasService.softDeleteNfe(deleteModal.id, deleteReason.trim(), user.id)
        setNfeList((prev) => prev.filter((n) => n.id !== deleteModal.id))
      } else {
        await admDespesasService.softDeleteNfse(deleteModal.id, deleteReason.trim(), user.id)
        setNfseList((prev) => prev.filter((n) => n.id !== deleteModal.id))
      }
      closeDeleteModal()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingDelete(false)
    }
  }

  const openNfeDetail = async (nfe: AdmNfeHeader) => {
    setLoadingDetail(true)
    try {
      const items = await admDespesasService.listNfeItems(nfe.id)
      setDetailNfe({ header: nfe, items })
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoadingDetail(false)
    }
  }

  const openNfseDetail = async (nfse: AdmNfseHeader) => {
    setLoadingDetail(true)
    try {
      const items = await admDespesasService.listNfseItems(nfse.id)
      setDetailNfse({ header: nfse, items })
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoadingDetail(false)
    }
  }

  const startEditItem = (tableType: 'nfe' | 'nfse', itemId: string, field: 'category' | 'cost_center', currentValue: string | null, context: 'modal' | 'list' = 'modal') => {
    setEditingItem({ tableType, itemId, field, context })
    setEditingValue(currentValue ?? '')
  }

  const cancelEditItem = () => {
    setEditingItem(null)
    setEditingValue('')
  }

  const handleSaveItemField = async () => {
    if (!editingItem) return
    const { tableType, itemId, field } = editingItem
    const newValue = editingValue || null

    const oldValue = field === 'category'
      ? (tableType === 'nfe'
        ? detailNfe?.items.find((i) => i.id === itemId)?.category_id ?? null
        : detailNfse?.items.find((i) => i.id === itemId)?.category_id ?? null)
      : (tableType === 'nfe'
        ? detailNfe?.items.find((i) => i.id === itemId)?.cost_center_id ?? null
        : detailNfse?.items.find((i) => i.id === itemId)?.cost_center_id ?? null)

    if (oldValue === newValue) {
      cancelEditItem()
      return
    }

    setSavingItem(itemId)
    try {
      const patch = field === 'category'
        ? { category_id: newValue }
        : { cost_center_id: newValue }

      if (tableType === 'nfe') {
        await admDespesasService.updateNfeItem(itemId, patch)
        setDetailNfe((prev) => prev ? {
          ...prev,
          items: prev.items.map((i) => i.id === itemId ? { ...i, ...patch } : i),
        } : prev)
        setAllNfeItems((prev) => prev.map((i) => i.id === itemId ? { ...i, ...patch } : i))
      } else {
        await admDespesasService.updateNfseItem(itemId, patch)
        setDetailNfse((prev) => prev ? {
          ...prev,
          items: prev.items.map((i) => i.id === itemId ? { ...i, ...patch } : i),
        } : prev)
        setAllNfseItems((prev) => prev.map((i) => i.id === itemId ? { ...i, ...patch } : i))
      }

      const fieldLabel = field === 'category' ? 'categoria' : 'centro de custo'
      const headerId = tableType === 'nfe' ? detailNfe?.header.id : detailNfse?.header.id
      await logUpdate(
        'other',
        itemId,
        `Atualizou ${fieldLabel} do item da ${tableType.toUpperCase()}`,
        { [field === 'category' ? 'category_id' : 'cost_center_id']: oldValue },
        { [field === 'category' ? 'category_id' : 'cost_center_id']: newValue },
        { additional_info: `${tableType.toUpperCase()} ID: ${headerId ?? ''}` }
      )

      cancelEditItem()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSavingItem(null)
    }
  }

  const openItemEditModal = (tableType: 'nfe' | 'nfse', item: AdmNfeItem | AdmNfseItem) => {
    const header = tableType === 'nfe' ? detailNfe?.header : detailNfse?.header
    if (!header) return
    setItemEditModal({
      tableType,
      itemId: item.id,
      headerId: header.id,
      headerNumber: header.number ?? header.id,
      description: item.description ?? '',
      ncm: tableType === 'nfe' ? ((item as AdmNfeItem).ncm ?? '') : '',
      unit: tableType === 'nfe' ? ((item as AdmNfeItem).unit ?? '') : '',
      serviceCode: tableType === 'nfse' ? ((item as AdmNfseItem).service_code ?? '') : '',
      quantity: item.quantity != null ? String(item.quantity) : '',
      unitValue: item.unit_value != null ? String(item.unit_value) : '',
      totalValue: item.total_value != null ? String(item.total_value) : '',
      categoryId: item.category_id ?? '',
      costCenterId: item.cost_center_id ?? '',
    })
  }

  const closeItemEditModal = () => {
    if (submittingItemEdit) return
    setItemEditModal(null)
  }

  const handleSaveItemEdit = async () => {
    if (!itemEditModal) return
    const categoryId = itemEditModal.categoryId || null
    const costCenterId = itemEditModal.costCenterId || null

    const oldItem = itemEditModal.tableType === 'nfe'
      ? detailNfe?.items.find((i) => i.id === itemEditModal.itemId)
      : detailNfse?.items.find((i) => i.id === itemEditModal.itemId)

    if (!oldItem) return

    setSubmittingItemEdit(true)
    try {
      if (itemEditModal.tableType === 'nfe') {
        const patch = {
          description: itemEditModal.description.trim() || null,
          ncm: itemEditModal.ncm.trim() || null,
          quantity: itemEditModal.quantity.trim() ? Number(itemEditModal.quantity) : null,
          unit: itemEditModal.unit.trim() || null,
          unit_value: itemEditModal.unitValue.trim() ? Number(itemEditModal.unitValue) : null,
          total_value: itemEditModal.totalValue.trim() ? Number(itemEditModal.totalValue) : null,
          category_id: categoryId,
          cost_center_id: costCenterId,
        }

        const oldPayload = {
          description: oldItem.description ?? null,
          ncm: (oldItem as AdmNfeItem).ncm ?? null,
          quantity: oldItem.quantity ?? null,
          unit: (oldItem as AdmNfeItem).unit ?? null,
          unit_value: oldItem.unit_value ?? null,
          total_value: oldItem.total_value ?? null,
          category_id: oldItem.category_id ?? null,
          cost_center_id: oldItem.cost_center_id ?? null,
        }

        if (JSON.stringify(oldPayload) === JSON.stringify(patch)) {
          setItemEditModal(null)
          return
        }

        await admDespesasService.updateNfeItem(itemEditModal.itemId, patch)
        setDetailNfe((prev) => prev ? {
          ...prev,
          items: prev.items.map((i) => i.id === itemEditModal.itemId ? { ...i, ...patch } : i),
        } : prev)
        setAllNfeItems((prev) => prev.map((i) => i.id === itemEditModal.itemId ? { ...i, ...patch } : i))

        await logUpdate(
          'other',
          itemEditModal.itemId,
          `Atualizou dados do item da NFE ${itemEditModal.headerNumber}`,
          oldPayload,
          patch,
          {
            additional_info: `NFE ID: ${itemEditModal.headerId} | Item: ${itemEditModal.description || itemEditModal.itemId}`,
          }
        )
      } else {
        const patch = {
          description: itemEditModal.description.trim() || null,
          service_code: itemEditModal.serviceCode.trim() || null,
          quantity: itemEditModal.quantity.trim() ? Number(itemEditModal.quantity) : null,
          unit_value: itemEditModal.unitValue.trim() ? Number(itemEditModal.unitValue) : null,
          total_value: itemEditModal.totalValue.trim() ? Number(itemEditModal.totalValue) : null,
          category_id: categoryId,
          cost_center_id: costCenterId,
        }

        const oldPayload = {
          description: oldItem.description ?? null,
          service_code: (oldItem as AdmNfseItem).service_code ?? null,
          quantity: oldItem.quantity ?? null,
          unit_value: oldItem.unit_value ?? null,
          total_value: oldItem.total_value ?? null,
          category_id: oldItem.category_id ?? null,
          cost_center_id: oldItem.cost_center_id ?? null,
        }

        if (JSON.stringify(oldPayload) === JSON.stringify(patch)) {
          setItemEditModal(null)
          return
        }

        await admDespesasService.updateNfseItem(itemEditModal.itemId, patch)
        setDetailNfse((prev) => prev ? {
          ...prev,
          items: prev.items.map((i) => i.id === itemEditModal.itemId ? { ...i, ...patch } : i),
        } : prev)
        setAllNfseItems((prev) => prev.map((i) => i.id === itemEditModal.itemId ? { ...i, ...patch } : i))

        await logUpdate(
          'other',
          itemEditModal.itemId,
          `Atualizou dados do serviço da NFSE ${itemEditModal.headerNumber}`,
          oldPayload,
          patch,
          {
            additional_info: `NFSE ID: ${itemEditModal.headerId} | Item: ${itemEditModal.description || itemEditModal.itemId}`,
          }
        )
      }

      setItemEditModal(null)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingItemEdit(false)
    }
  }

  const handleSoftDeleteItem = async (tableType: 'nfe' | 'nfse', item: AdmNfeItem | AdmNfseItem) => {
    if (!user?.id) return
    const header = tableType === 'nfe' ? detailNfe?.header : detailNfse?.header
    if (!header) return

    const confirmed = window.confirm(`Confirma a exclusão do item "${item.description ?? item.id}"?`)
    if (!confirmed) return

    setSavingItem(item.id)
    try {
      if (tableType === 'nfe') {
        await admDespesasService.softDeleteNfeItem(item.id, user.id)
        setDetailNfe((prev) => prev ? { ...prev, items: prev.items.filter((i) => i.id !== item.id) } : prev)
        setAllNfeItems((prev) => prev.filter((i) => i.id !== item.id))
      } else {
        await admDespesasService.softDeleteNfseItem(item.id, user.id)
        setDetailNfse((prev) => prev ? { ...prev, items: prev.items.filter((i) => i.id !== item.id) } : prev)
        setAllNfseItems((prev) => prev.filter((i) => i.id !== item.id))
      }

      await logDelete(
        'other',
        item.id,
        `Excluiu item da ${tableType.toUpperCase()} ${header.number ?? header.id}`,
        {
          additional_info: `${tableType.toUpperCase()} ID: ${header.id} | Item: ${item.description ?? item.id}`,
          old_values: {
            description: item.description ?? null,
            category_id: item.category_id ?? null,
            cost_center_id: item.cost_center_id ?? null,
          },
        }
      )
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSavingItem(null)
    }
  }

  const openUploadModal = () => {
    setUploadFiles([])
    setIsProcessing(false)
    setProcessingDone(false)
    setProcessingStartedAt(null)
    setProcessingFinishedAt(null)
    setUploadModalOpen(true)
  }

  const closeUploadModal = () => {
    if (isProcessing) return
    setUploadModalOpen(false)
    setUploadFiles([])
    setIsProcessing(false)
    setProcessingDone(false)
    setProcessingStartedAt(null)
    setProcessingFinishedAt(null)
    void loadData()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? [])
    if (selected.length === 0) return
    setUploadFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.file.name))
      const newEntries: FileStatus[] = selected
        .filter((f) => !existingNames.has(f.name))
        .map((f) => ({ file: f, status: 'pending' }))
      return [...prev, ...newEntries]
    })
    e.target.value = ''
  }

  const removeFile = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const sendFile = async (file: File): Promise<void> => {
    const formData = new FormData()
    formData.append('arquivo', file)
    formData.append('nome_arquivo', file.name)

    const response = await fetch(N8N_WEBHOOK_URL, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    let retorno = 'sucesso'
    try {
      const json = await response.json() as Array<{ retorno?: string }> | { retorno?: string }
      const first = Array.isArray(json) ? json[0] : json
      retorno = (first?.retorno ?? 'sucesso').toString().trim()
    } catch {
      // resposta sem JSON válido — trata como sucesso
    }

    if (retorno.toLowerCase() !== 'sucesso') {
      throw new Error(retorno)
    }
  }

  const handleUploadSubmit = async () => {
    if (uploadFiles.length === 0 || isProcessing) return
    setIsProcessing(true)
    setProcessingDone(false)
    setProcessingStartedAt(new Date())
    setProcessingFinishedAt(null)

    for (let i = 0; i < uploadFiles.length; i++) {
      const entry = uploadFiles[i]
      if (entry.status === 'success') continue

      if (i > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, 1000))
      }

      setUploadFiles((prev) =>
        prev.map((f, idx) => (idx === i ? { ...f, status: 'sending', error: undefined } : f))
      )

      try {
        await sendFile(entry.file)
        setUploadFiles((prev) =>
          prev.map((f, idx) => (idx === i ? { ...f, status: 'success', finishedAt: new Date() } : f))
        )
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Erro ao enviar.'
        const isNetworkError = msg === 'Failed to fetch'

        if (isNetworkError) {
          setUploadFiles((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, status: 'sending', error: undefined } : f))
          )
          await new Promise<void>((resolve) => setTimeout(resolve, 5000))
          try {
            await sendFile(entry.file)
            setUploadFiles((prev) =>
              prev.map((f, idx) => (idx === i ? { ...f, status: 'success', finishedAt: new Date() } : f))
            )
          } catch (retryError) {
            const retryMsg = retryError instanceof Error ? retryError.message : 'Erro ao enviar.'
            setUploadFiles((prev) =>
              prev.map((f, idx) => (idx === i ? { ...f, status: 'error', error: retryMsg, finishedAt: new Date() } : f))
            )
          }
        } else {
          setUploadFiles((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, status: 'error', error: msg, finishedAt: new Date() } : f))
          )
        }
      }
    }

    const finishedAt = new Date()
    setProcessingFinishedAt(finishedAt)
    setIsProcessing(false)
    setProcessingDone(true)
    void loadData()
  }

  const tabClass = (tab: ActiveTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-blue-600 text-blue-600 dark:text-blue-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  const subTabClass = (active: boolean) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      active
        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    }`

  // Lógica de filtros e paginação - Notas NF-e
  const filteredNfeNotes = React.useMemo(() => {
    return nfeList.filter((nfe) => {
      const issuerLower = nfeItemsIssuer.toLowerCase()
      const numberLower = nfeItemsNoteNumber.toLowerCase()
      const accessKeyNormalized = nfeItemsAccessKey.replace(/\W/g, '').toLowerCase()
      const fileNameLower = nfeItemsFileName.toLowerCase()
      if (issuerLower && !(nfe.issuer_name ?? '').toLowerCase().includes(issuerLower)) return false
      if (numberLower && !(nfe.number ?? '').toLowerCase().includes(numberLower)) return false
      if (accessKeyNormalized && !((nfe.access_key ?? '').replace(/\W/g, '').toLowerCase().includes(accessKeyNormalized))) return false
      if (fileNameLower && !(nfe.file_name ?? '').toLowerCase().includes(fileNameLower)) return false
      return true
    })
  }, [nfeList, nfeItemsIssuer, nfeItemsNoteNumber, nfeItemsAccessKey, nfeItemsFileName])

  const sortedNfeNotes = React.useMemo(() => {
    return [...filteredNfeNotes].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (nfeNotesSort === 'number') { av = a.number ?? ''; bv = b.number ?? '' }
      else if (nfeNotesSort === 'issuer_name') { av = (a.issuer_name ?? '').toLowerCase(); bv = (b.issuer_name ?? '').toLowerCase() }
      else if (nfeNotesSort === 'issue_date') { av = a.issue_date ?? ''; bv = b.issue_date ?? '' }
      else if (nfeNotesSort === 'total_value') { av = a.total_value ?? 0; bv = b.total_value ?? 0 }
      if (av < bv) return nfeNotesSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfeNotesSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredNfeNotes, nfeNotesSort, nfeNotesSortOrder])

  const nfeNotesPagesCount = Math.max(1, Math.ceil(sortedNfeNotes.length / PAGE_SIZE))
  const nfeNotesStart = (nfeNotesPage - 1) * PAGE_SIZE
  const paginatedNfeNotes = sortedNfeNotes.slice(nfeNotesStart, nfeNotesStart + PAGE_SIZE)

  // Lógica de filtros e paginação - Itens NF-e
  const nfeHeaderMap = React.useMemo(() => new Map(nfeList.map((h) => [h.id, h])), [nfeList])

  const filteredNfeItems = React.useMemo(() => {
    return allNfeItems.filter((item) => {
      const header = nfeHeaderMap.get(item.nfe_id)
      if (!header) return false
      const searchLower = nfeItemsSearch.toLowerCase()
      const issuerLower = nfeItemsIssuer.toLowerCase()
      const noteNumberLower = nfeItemsNoteNumber.toLowerCase()
      const accessKeyNormalized = nfeItemsAccessKey.replace(/\W/g, '').toLowerCase()
      const fileNameLower = nfeItemsFileName.toLowerCase()
      if (searchLower && !(item.description ?? '').toLowerCase().includes(searchLower)) return false
      if (issuerLower && !(header?.issuer_name ?? '').toLowerCase().includes(issuerLower)) return false
      if (noteNumberLower && !(header?.number ?? '').toLowerCase().includes(noteNumberLower)) return false
      if (accessKeyNormalized && !((header?.access_key ?? '').replace(/\W/g, '').toLowerCase().includes(accessKeyNormalized))) return false
      if (fileNameLower && !((header?.file_name ?? '').toLowerCase().includes(fileNameLower))) return false
      if (nfeItemsCategory && item.category_id !== nfeItemsCategory) return false
      if (nfeItemsCostCenter && item.cost_center_id !== nfeItemsCostCenter) return false
      return true
    })
  }, [allNfeItems, nfeHeaderMap, nfeItemsSearch, nfeItemsIssuer, nfeItemsNoteNumber, nfeItemsAccessKey, nfeItemsFileName, nfeItemsCategory, nfeItemsCostCenter])

  const sortedNfeItems = React.useMemo(() => {
    return [...filteredNfeItems].sort((a, b) => {
      const ha = nfeHeaderMap.get(a.nfe_id)
      const hb = nfeHeaderMap.get(b.nfe_id)
      let av: string | number = ''
      let bv: string | number = ''
      if (nfeItemsSort === 'number') { av = ha?.number ?? ''; bv = hb?.number ?? '' }
      else if (nfeItemsSort === 'issuer_name') { av = (ha?.issuer_name ?? '').toLowerCase(); bv = (hb?.issuer_name ?? '').toLowerCase() }
      else if (nfeItemsSort === 'description') { av = (a.description ?? '').toLowerCase(); bv = (b.description ?? '').toLowerCase() }
      else if (nfeItemsSort === 'quantity') { av = a.quantity ?? 0; bv = b.quantity ?? 0 }
      else if (nfeItemsSort === 'unit_value') { av = a.unit_value ?? 0; bv = b.unit_value ?? 0 }
      else if (nfeItemsSort === 'total_value') { av = a.total_value ?? 0; bv = b.total_value ?? 0 }
      if (av < bv) return nfeItemsSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfeItemsSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredNfeItems, nfeHeaderMap, nfeItemsSort, nfeItemsSortOrder])

  const nfeItemsPagesCount = Math.max(1, Math.ceil(sortedNfeItems.length / PAGE_SIZE))
  const nfeItemsStart = (nfeItemsPage - 1) * PAGE_SIZE
  const paginatedNfeItems = sortedNfeItems.slice(nfeItemsStart, nfeItemsStart + PAGE_SIZE)

  const nfeItemsCountByHeader = React.useMemo(() => {
    return allNfeItems.reduce((acc, item) => {
      acc.set(item.nfe_id, (acc.get(item.nfe_id) ?? 0) + 1)
      return acc
    }, new Map<string, number>())
  }, [allNfeItems])

  // Lógica de filtros e paginação - Notas NFS-e
  const filteredNfseNotes = React.useMemo(() => {
    return nfseList.filter((nfse) => {
      const providerLower = nfseItemsProvider.toLowerCase()
      const numberLower = nfseItemsNoteNumber.toLowerCase()
      const accessKeyNormalized = nfseItemsAccessKey.replace(/\W/g, '').toLowerCase()
      const fileNameLower = nfseItemsFileName.toLowerCase()
      if (providerLower && !(nfse.provider_name ?? '').toLowerCase().includes(providerLower)) return false
      if (numberLower && !(nfse.number ?? '').toLowerCase().includes(numberLower)) return false
      if (accessKeyNormalized && !((nfse.access_key ?? '').replace(/\W/g, '').toLowerCase().includes(accessKeyNormalized))) return false
      if (fileNameLower && !(nfse.file_name ?? '').toLowerCase().includes(fileNameLower)) return false
      return true
    })
  }, [nfseList, nfseItemsProvider, nfseItemsNoteNumber, nfseItemsAccessKey, nfseItemsFileName])

  const sortedNfseNotes = React.useMemo(() => {
    return [...filteredNfseNotes].sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (nfseNotesSort === 'number') { av = a.number ?? ''; bv = b.number ?? '' }
      else if (nfseNotesSort === 'provider_name') { av = (a.provider_name ?? '').toLowerCase(); bv = (b.provider_name ?? '').toLowerCase() }
      else if (nfseNotesSort === 'issue_date') { av = a.issue_date ?? ''; bv = b.issue_date ?? '' }
      else if (nfseNotesSort === 'net_value') { av = getNfseDisplayTotal(a) ?? 0; bv = getNfseDisplayTotal(b) ?? 0 }
      if (av < bv) return nfseNotesSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfseNotesSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredNfseNotes, nfseNotesSort, nfseNotesSortOrder])

  const nfseNotesPagesCount = Math.max(1, Math.ceil(sortedNfseNotes.length / PAGE_SIZE))
  const nfseNotesStart = (nfseNotesPage - 1) * PAGE_SIZE
  const paginatedNfseNotes = sortedNfseNotes.slice(nfseNotesStart, nfseNotesStart + PAGE_SIZE)

  // Lógica de filtros e paginação - Itens NFS-e
  const nfseHeaderMap = React.useMemo(() => new Map(nfseList.map((h) => [h.id, h])), [nfseList])

  const filteredNfseItems = React.useMemo(() => {
    return allNfseItems.filter((item) => {
      const header = nfseHeaderMap.get(item.nfse_id)
      if (!header) return false
      const searchLower = nfseItemsSearch.toLowerCase()
      const providerLower = nfseItemsProvider.toLowerCase()
      const noteNumberLower = nfseItemsNoteNumber.toLowerCase()
      const accessKeyNormalized = nfseItemsAccessKey.replace(/\W/g, '').toLowerCase()
      const fileNameLower = nfseItemsFileName.toLowerCase()
      const serviceCodeLower = nfseItemsServiceCode.toLowerCase()
      if (searchLower && !(item.description ?? '').toLowerCase().includes(searchLower)) return false
      if (providerLower && !(header?.provider_name ?? '').toLowerCase().includes(providerLower)) return false
      if (noteNumberLower && !(header?.number ?? '').toLowerCase().includes(noteNumberLower)) return false
      if (accessKeyNormalized && !((header?.access_key ?? '').replace(/\W/g, '').toLowerCase().includes(accessKeyNormalized))) return false
      if (fileNameLower && !((header?.file_name ?? '').toLowerCase().includes(fileNameLower))) return false
      if (serviceCodeLower && !(item.service_code ?? '').toLowerCase().includes(serviceCodeLower)) return false
      if (nfseItemsCategory && item.category_id !== nfseItemsCategory) return false
      if (nfseItemsCostCenter && item.cost_center_id !== nfseItemsCostCenter) return false
      return true
    })
  }, [allNfseItems, nfseHeaderMap, nfseItemsSearch, nfseItemsProvider, nfseItemsNoteNumber, nfseItemsAccessKey, nfseItemsFileName, nfseItemsServiceCode, nfseItemsCategory, nfseItemsCostCenter])

  const sortedNfseItems = React.useMemo(() => {
    return [...filteredNfseItems].sort((a, b) => {
      const ha = nfseHeaderMap.get(a.nfse_id)
      const hb = nfseHeaderMap.get(b.nfse_id)
      let av: string | number = ''
      let bv: string | number = ''
      if (nfseItemsSort === 'number') { av = ha?.number ?? ''; bv = hb?.number ?? '' }
      else if (nfseItemsSort === 'provider_name') { av = (ha?.provider_name ?? '').toLowerCase(); bv = (hb?.provider_name ?? '').toLowerCase() }
      else if (nfseItemsSort === 'description') { av = (a.description ?? '').toLowerCase(); bv = (b.description ?? '').toLowerCase() }
      else if (nfseItemsSort === 'quantity') { av = a.quantity ?? 0; bv = b.quantity ?? 0 }
      else if (nfseItemsSort === 'unit_value') { av = a.unit_value ?? 0; bv = b.unit_value ?? 0 }
      else if (nfseItemsSort === 'total_value') { av = a.total_value ?? 0; bv = b.total_value ?? 0 }
      if (av < bv) return nfseItemsSortOrder === 'asc' ? -1 : 1
      if (av > bv) return nfseItemsSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredNfseItems, nfseHeaderMap, nfseItemsSort, nfseItemsSortOrder])

  const nfseItemsPagesCount = Math.max(1, Math.ceil(sortedNfseItems.length / PAGE_SIZE))
  const nfseItemsStart = (nfseItemsPage - 1) * PAGE_SIZE
  const paginatedNfseItems = sortedNfseItems.slice(nfseItemsStart, nfseItemsStart + PAGE_SIZE)

  const nfeNotesTotalValue = React.useMemo(
    () => filteredNfeNotes.reduce((sum, nfe) => sum + (nfe.total_value ?? 0), 0),
    [filteredNfeNotes],
  )
  const nfeItemsUnitValueTotal = React.useMemo(
    () => filteredNfeItems.reduce((sum, item) => sum + (item.unit_value ?? 0), 0),
    [filteredNfeItems],
  )
  const nfeItemsQuantityTotal = React.useMemo(
    () => filteredNfeItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0),
    [filteredNfeItems],
  )
  const nfeItemsTotalValue = React.useMemo(
    () => filteredNfeItems.reduce((sum, item) => sum + (item.total_value ?? 0), 0),
    [filteredNfeItems],
  )
  const nfseNotesTotalValue = React.useMemo(
    () => filteredNfseNotes.reduce((sum, nfse) => sum + (getNfseDisplayTotal(nfse) ?? 0), 0),
    [filteredNfseNotes],
  )
  const nfseItemsUnitValueTotal = React.useMemo(
    () => filteredNfseItems.reduce((sum, item) => sum + (item.unit_value ?? 0), 0),
    [filteredNfseItems],
  )
  const nfseItemsQuantityTotal = React.useMemo(
    () => filteredNfseItems.reduce((sum, item) => sum + (item.quantity ?? 0), 0),
    [filteredNfseItems],
  )
  const nfseItemsTotalValue = React.useMemo(
    () => filteredNfseItems.reduce((sum, item) => sum + (item.total_value ?? 0), 0),
    [filteredNfseItems],
  )
  const nfeItemsUnitValueAverage = React.useMemo(() => {
    const n = filteredNfeItems.length
    if (n === 0) return 0
    return filteredNfeItems.reduce((sum, item) => sum + (item.unit_value ?? 0), 0) / n
  }, [filteredNfeItems])
  const nfseItemsUnitValueAverage = React.useMemo(() => {
    const n = filteredNfseItems.length
    if (n === 0) return 0
    return filteredNfseItems.reduce((sum, item) => sum + (item.unit_value ?? 0), 0) / n
  }, [filteredNfseItems])

  const renderTotalsSummary = (
    totals: Array<{ label: string; value: number; kind?: 'currency' | 'quantity' }>,
  ) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <div className="flex flex-nowrap items-stretch gap-2 sm:gap-3 overflow-x-auto pb-0.5">
        {totals.map((item) => (
          <div
            key={item.label}
            className="flex min-w-[6.75rem] flex-1 flex-col rounded-lg border border-gray-200 dark:border-gray-700 px-2 sm:min-w-0 sm:px-3 py-2"
          >
            <Text size="xs" color="muted" className="uppercase tracking-wide">
              {item.label}
            </Text>
            <Text size="sm" className="font-semibold text-gray-900 dark:text-white mt-0.5 tabular-nums">
              {item.kind === 'quantity' ? formatQuantity(item.value) : formatCurrency(item.value)}
            </Text>
          </div>
        ))}
      </div>
    </div>
  )

  const renderPagination = (
    currentPage: number,
    pagesCount: number,
    total: number,
    setPage: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    if (pagesCount <= 1) return null
    return (
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <Text size="sm" color="muted">
          Página {currentPage} de {pagesCount} ({total} {total === 1 ? 'registro' : 'registros'})
        </Text>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
            Anterior
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pagesCount, p + 1))} disabled={currentPage === pagesCount}>
            Próximo
          </Button>
        </div>
      </div>
    )
  }

  const renderInlineItemEdit = (
    item: AdmNfeItem | AdmNfseItem,
    tableType: 'nfe' | 'nfse',
    field: 'category' | 'cost_center',
    context: 'modal' | 'list',
  ) => {
    const isEditing = editingItem?.itemId === item.id && editingItem.field === field && editingItem.tableType === tableType && editingItem.context === context
    const isSaving = savingItem === item.id
    const currentId = field === 'category' ? item.category_id : item.cost_center_id
    const map = field === 'category' ? categoryMap : costCenterMap
    const options = field === 'category' ? categorias : centrosCusto
    const accentColor = field === 'category' ? 'blue' : 'purple'
    const noneLabel = field === 'category' ? '— Nenhuma —' : '— Nenhum —'

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <select
            autoFocus
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            disabled={isSaving}
            className={`flex-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-${accentColor}-500`}
          >
            <option value="">{noneLabel}</option>
            {options.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void handleSaveItemField()}
            disabled={isSaving}
            className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
            title="Salvar"
          >
            <CheckCircleIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={cancelEditItem}
            disabled={isSaving}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            title="Cancelar"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )
    }

    return (
      <div className="group flex items-center gap-1">
        {currentId ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${accentColor}-100 text-${accentColor}-700 dark:bg-${accentColor}-900/30 dark:text-${accentColor}-300`}>
            {map.get(currentId) ?? currentId}
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
        )}
        <button
          type="button"
          onClick={() => startEditItem(tableType, item.id, field, currentId ?? null, context)}
          className={`opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-${accentColor}-500 transition-opacity`}
          title={field === 'category' ? 'Editar categoria' : 'Editar centro de custo'}
        >
          <PencilIcon className="h-3 w-3" />
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName="adm_despesas"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
              <div>
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                  {moduleInfo?.display_name || 'ADM • Despesas'}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  Em notas com baixa qualidade, dados ilegíveis ou situações similares, a importação por IA pode apresentar diferenças em relação ao original ou confundir campos no documento. Em caso de dúvidas, consulte o documento original.
                </Text>
                {moduleInfo?.description && (
                  <Text size="sm" color="muted" className="mt-1">
                    {moduleInfo.description}
                  </Text>
                )}
              </div>
              <Button variant="solid" size="md" onClick={openUploadModal} className="flex-shrink-0">
                <PlusIcon className="h-4 w-4" />
                Cadastrar Despesas
              </Button>
            </div>

            {/* Tabs */}
            <div className="px-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8">
                <button
                  type="button"
                  onClick={() => setActiveTab('nfe')}
                  className={tabClass('nfe')}
                >
                  <span className="inline-flex items-center gap-1.5">
                    Compras NF-e
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('nfse')}
                  className={tabClass('nfse')}
                >
                  <span className="inline-flex items-center gap-1.5">
                    Serviços NFS-e
                  </span>
                </button>
              </nav>
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {pageError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    <Text size="sm">{pageError}</Text>
                  </div>
                )}

                {/* ABA NF-e */}
                {activeTab === 'nfe' && (
                  <>
                    <Text size="sm" color="muted">
                      Lista e organiza despesas de compras (NF-e), com filtros, detalhamento e impressão.
                    </Text>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="mb-3">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <Input type="text" placeholder="Emitente" value={nfeItemsIssuer} onChange={(e) => { setNfeItemsIssuer(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} />
                        <Input type="text" placeholder="Nº da nota" value={nfeItemsNoteNumber} onChange={(e) => { setNfeItemsNoteNumber(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} />
                        <Input type="text" placeholder="Chave de acesso" value={nfeItemsAccessKey} onChange={(e) => { setNfeItemsAccessKey(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} />
                        <Input type="text" placeholder="Nome do arquivo" value={nfeItemsFileName} onChange={(e) => { setNfeItemsFileName(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} />
                        <select value={nfeItemsCategory} onChange={(e) => { setNfeItemsCategory(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Categoria: Todas</option>
                          {categorias.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <select value={nfeItemsCostCenter} onChange={(e) => { setNfeItemsCostCenter(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Centro de Custo: Todos</option>
                          {centrosCusto.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <div className="col-span-full flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setNfeItemsSearch(''); setNfeItemsIssuer(''); setNfeItemsNoteNumber(''); setNfeItemsAccessKey(''); setNfeItemsFileName(''); setNfeItemsCategory(''); setNfeItemsCostCenter(''); setNfeNotesSearch(''); setNfeNotesCnpj(''); setNfeNotesNumber(''); setNfeNotesAccessKey(''); setNfeNotesFileName(''); setNfeNotesDateFrom(''); setNfeNotesDateTo(''); setNfeItemsPage(1); setNfeNotesPage(1) }}>
                            Limpar filtros
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openPrintDialog(nfeSubTab === 'notes' ? 'nfe_notes' : 'nfe_items')}>
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Imprimir
                          </Button>
                        </div>
                      </div>
                    </div>
                    {/* Subabas NF-e */}
                    <div className="border-b border-gray-200 dark:border-gray-700">
                      <nav className="flex space-x-6">
                        <button type="button" onClick={() => setNfeSubTab('notes')} className={subTabClass(nfeSubTab === 'notes')}>
                          <span className="inline-flex items-center gap-1.5">
                            Notas Fiscais
                            <InfoTip content="Relação das notas NF-e com dados de emissão, emitente e valores totais." />
                          </span>
                        </button>
                        <button type="button" onClick={() => setNfeSubTab('items')} className={subTabClass(nfeSubTab === 'items')}>
                          <span className="inline-flex items-center gap-1.5">
                            Itens
                            <InfoTip content="Relação item a item das NF-e para análise por descrição, categoria e centro de custo." />
                          </span>
                          {filteredNfeItems.length > 0 && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              {filteredNfeItems.length}
                            </span>
                          )}
                        </button>
                      </nav>
                    </div>

                    {/* Subaba Notas NF-e */}
                    {nfeSubTab === 'notes' && (
                      <>
                        {renderTotalsSummary([
                          { label: 'Soma Valor Total', value: nfeNotesTotalValue },
                        ])}
                        {/* Tabela */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                          {paginatedNfeNotes.length === 0 ? (
                            <div className="px-4 py-12 text-center">
                              <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                              <Text size="sm" color="muted">{nfeList.length === 0 ? 'Nenhuma NF-e cadastrada.' : 'Nenhuma nota encontrada com os filtros aplicados.'}</Text>
                            </div>
                          ) : (
                            <>
                              <table className="w-full min-w-[800px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <SortableTableHeader sortKey="number" currentSort={nfeNotesSort} sortOrder={nfeNotesSortOrder} onSort={toggleSort(nfeNotesSort, setNfeNotesSort, nfeNotesSortOrder, setNfeNotesSortOrder, setNfeNotesPage)} className="px-4 py-3">Número</SortableTableHeader>
                                    <SortableTableHeader sortKey="issuer_name" currentSort={nfeNotesSort} sortOrder={nfeNotesSortOrder} onSort={toggleSort(nfeNotesSort, setNfeNotesSort, nfeNotesSortOrder, setNfeNotesSortOrder, setNfeNotesPage)} className="px-4 py-3">Emitente</SortableTableHeader>
                                    <SortableTableHeader sortKey="issue_date" currentSort={nfeNotesSort} sortOrder={nfeNotesSortOrder} onSort={toggleSort(nfeNotesSort, setNfeNotesSort, nfeNotesSortOrder, setNfeNotesSortOrder, setNfeNotesPage)} className="px-4 py-3">Emissão</SortableTableHeader>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Nº Itens</th>
                                    <SortableTableHeader sortKey="total_value" currentSort={nfeNotesSort} sortOrder={nfeNotesSortOrder} onSort={toggleSort(nfeNotesSort, setNfeNotesSort, nfeNotesSortOrder, setNfeNotesSortOrder, setNfeNotesPage)} className="px-4 py-3 text-right">Valor Total</SortableTableHeader>
                                    {(canDelete || canEdit) && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {paginatedNfeNotes.map((nfe) => (
                                    <tr key={nfe.id} onClick={() => void openNfeDetail(nfe)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                                        {nfe.number || '—'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[320px] truncate" title={nfe.issuer_name ?? ''}>{nfe.issuer_name ? (nfe.issuer_name.length > 40 ? nfe.issuer_name.slice(0, 40) + '…' : nfe.issuer_name) : '—'}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">{formatDate(nfe.issue_date)}</td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right whitespace-nowrap">{nfeItemsCountByHeader.get(nfe.id) ?? 0}</td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right whitespace-nowrap font-medium">{formatCurrency(nfe.total_value)}</td>
                                      {(canDelete || canEdit) && (
                                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                          <div className="flex items-center justify-center gap-2">
                                            {canEdit && (
                                              <button
                                                type="button"
                                                title="Editar cabeçalho"
                                                onClick={() => openHeaderEditModal('nfe', nfe)}
                                                className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                              >
                                                <PencilIcon className="h-4 w-4" />
                                              </button>
                                            )}
                                            {canDelete && (
                                              <button type="button" title="Excluir" onClick={() => openDeleteModal('nfe', nfe.id, `NF-e ${nfe.number || nfe.id}`)} className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors">
                                                <TrashIcon className="h-4 w-4" />
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {renderPagination(nfeNotesPage, nfeNotesPagesCount, filteredNfeNotes.length, setNfeNotesPage)}
                            </>
                          )}
                        </div>
                      </>
                    )}

                    {/* Subaba Itens NF-e */}
                    {nfeSubTab === 'items' && (
                      <>
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                          <Input type="text" placeholder="Descrição Produto" value={nfeItemsSearch} onChange={(e) => { setNfeItemsSearch(e.target.value); setNfeItemsPage(1); setNfeNotesPage(1) }} />
                        </div>
                        {renderTotalsSummary([
                          { label: 'Soma Qtd', value: nfeItemsQuantityTotal, kind: 'quantity' },
                          { label: 'Soma Vl. Unit.', value: nfeItemsUnitValueTotal },
                          { label: 'Média Vl. Unit.', value: nfeItemsUnitValueAverage },
                          { label: 'Soma Total', value: nfeItemsTotalValue },
                        ])}
                        {/* Tabela */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                          {paginatedNfeItems.length === 0 ? (
                            <div className="px-4 py-12 text-center">
                              <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                              <Text size="sm" color="muted">{allNfeItems.length === 0 ? 'Nenhum item cadastrado.' : 'Nenhum item encontrado com os filtros aplicados.'}</Text>
                            </div>
                          ) : (
                            <>
                              <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <SortableTableHeader sortKey="number" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3">Nota</SortableTableHeader>
                                    <SortableTableHeader sortKey="issuer_name" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3">Emitente</SortableTableHeader>
                                    <SortableTableHeader sortKey="description" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3">Descrição</SortableTableHeader>
                                    <SortableTableHeader sortKey="quantity" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3 text-right">Qtd</SortableTableHeader>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Un</th>
                                    <SortableTableHeader sortKey="unit_value" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3 text-right">Vl. Unit.</SortableTableHeader>
                                    <SortableTableHeader sortKey="total_value" currentSort={nfeItemsSort} sortOrder={nfeItemsSortOrder} onSort={toggleSort(nfeItemsSort, setNfeItemsSort, nfeItemsSortOrder, setNfeItemsSortOrder, setNfeItemsPage)} className="px-4 py-3 text-right">Total</SortableTableHeader>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Centro de Custo</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {paginatedNfeItems.map((item) => {
                                    const header = nfeHeaderMap.get(item.nfe_id)
                                    return (
                                      <tr key={item.id} onClick={() => header && void openNfeDetail(header)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap font-medium">{header?.number || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[160px] truncate" title={header?.issuer_name ?? ''}>{header?.issuer_name || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[220px] truncate" title={item.description ?? ''}>{item.description || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{item.quantity ?? '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.unit || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(item.unit_value)}</td>
                                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(item.total_value)}</td>
                                        <td className="px-4 py-3 min-w-[160px]" onClick={(e) => e.stopPropagation()}>{renderInlineItemEdit(item, 'nfe', 'category', 'list')}</td>
                                        <td className="px-4 py-3 min-w-[160px]" onClick={(e) => e.stopPropagation()}>{renderInlineItemEdit(item, 'nfe', 'cost_center', 'list')}</td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                              {renderPagination(nfeItemsPage, nfeItemsPagesCount, filteredNfeItems.length, setNfeItemsPage)}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* ABA NFS-e */}
                {activeTab === 'nfse' && (
                  <>
                    <Text size="sm" color="muted">
                      Lista e organiza despesas de serviços (NFS-e), com filtros, detalhamento e impressão.
                    </Text>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="mb-3">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <Input type="text" placeholder="Prestador" value={nfseItemsProvider} onChange={(e) => { setNfseItemsProvider(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} />
                        <Input type="text" placeholder="Nº da nota" value={nfseItemsNoteNumber} onChange={(e) => { setNfseItemsNoteNumber(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} />
                        <Input type="text" placeholder="Chave de acesso" value={nfseItemsAccessKey} onChange={(e) => { setNfseItemsAccessKey(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} />
                        <Input type="text" placeholder="Nome do arquivo" value={nfseItemsFileName} onChange={(e) => { setNfseItemsFileName(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} />
                        <select value={nfseItemsCategory} onChange={(e) => { setNfseItemsCategory(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Categoria: Todas</option>
                          {categorias.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <select value={nfseItemsCostCenter} onChange={(e) => { setNfseItemsCostCenter(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm">
                          <option value="">Centro de Custo: Todos</option>
                          {centrosCusto.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <div className="col-span-full flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setNfseItemsSearch(''); setNfseItemsProvider(''); setNfseItemsNoteNumber(''); setNfseItemsAccessKey(''); setNfseItemsFileName(''); setNfseItemsServiceCode(''); setNfseItemsCategory(''); setNfseItemsCostCenter(''); setNfseNotesSearch(''); setNfseNotesCnpj(''); setNfseNotesNumber(''); setNfseNotesAccessKey(''); setNfseNotesFileName(''); setNfseNotesDateFrom(''); setNfseNotesDateTo(''); setNfseItemsPage(1); setNfseNotesPage(1) }}>
                            Limpar filtros
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openPrintDialog(nfseSubTab === 'notes' ? 'nfse_notes' : 'nfse_items')}>
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Imprimir
                          </Button>
                        </div>
                      </div>
                    </div>
                    {/* Subabas NFS-e */}
                    <div className="border-b border-gray-200 dark:border-gray-700">
                      <nav className="flex space-x-6">
                        <button type="button" onClick={() => setNfseSubTab('notes')} className={subTabClass(nfseSubTab === 'notes')}>
                          <span className="inline-flex items-center gap-1.5">
                            Notas Fiscais
                            <InfoTip content="Relação das notas NFS-e com competência, prestador e valor total considerado nos cálculos." />
                          </span>
                        </button>
                        <button type="button" onClick={() => setNfseSubTab('items')} className={subTabClass(nfseSubTab === 'items')}>
                          <span className="inline-flex items-center gap-1.5">
                            Serviços
                            <InfoTip content="Relação detalhada dos serviços das NFS-e para análise por categoria e centro de custo." />
                          </span>
                          {filteredNfseItems.length > 0 && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              {filteredNfseItems.length}
                            </span>
                          )}
                        </button>
                      </nav>
                    </div>

                    {/* Subaba Notas NFS-e */}
                    {nfseSubTab === 'notes' && (
                      <>
                        {renderTotalsSummary([
                          { label: 'Soma Valor Total', value: nfseNotesTotalValue },
                        ])}
                        {/* Tabela */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                          {paginatedNfseNotes.length === 0 ? (
                            <div className="px-4 py-12 text-center">
                              <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                              <Text size="sm" color="muted">{nfseList.length === 0 ? 'Nenhuma NFS-e cadastrada.' : 'Nenhuma nota encontrada com os filtros aplicados.'}</Text>
                            </div>
                          ) : (
                            <>
                              <table className="w-full min-w-[800px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <SortableTableHeader sortKey="number" currentSort={nfseNotesSort} sortOrder={nfseNotesSortOrder} onSort={toggleSort(nfseNotesSort, setNfseNotesSort, nfseNotesSortOrder, setNfseNotesSortOrder, setNfseNotesPage)} className="px-4 py-3">Número</SortableTableHeader>
                                    <SortableTableHeader sortKey="provider_name" currentSort={nfseNotesSort} sortOrder={nfseNotesSortOrder} onSort={toggleSort(nfseNotesSort, setNfseNotesSort, nfseNotesSortOrder, setNfseNotesSortOrder, setNfseNotesPage)} className="px-4 py-3">Prestador</SortableTableHeader>
                                    <SortableTableHeader sortKey="issue_date" currentSort={nfseNotesSort} sortOrder={nfseNotesSortOrder} onSort={toggleSort(nfseNotesSort, setNfseNotesSort, nfseNotesSortOrder, setNfseNotesSortOrder, setNfseNotesPage)} className="px-4 py-3">Competência</SortableTableHeader>
                                    <SortableTableHeader sortKey="net_value" currentSort={nfseNotesSort} sortOrder={nfseNotesSortOrder} onSort={toggleSort(nfseNotesSort, setNfseNotesSort, nfseNotesSortOrder, setNfseNotesSortOrder, setNfseNotesPage)} className="px-4 py-3 text-right">Valor Total</SortableTableHeader>
                                    {(canDelete || canEdit) && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {paginatedNfseNotes.map((nfse) => (
                                    <tr key={nfse.id} onClick={() => void openNfseDetail(nfse)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">{nfse.number || '—'}</td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[320px] truncate" title={nfse.provider_name ?? ''}>{nfse.provider_name ? (nfse.provider_name.length > 40 ? nfse.provider_name.slice(0, 40) + '…' : nfse.provider_name) : '—'}</td>
                                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                        {nfse.competence_date ? new Date(nfse.competence_date + 'T00:00:00').toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' }) : '—'}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right whitespace-nowrap font-medium">{formatCurrency(getNfseDisplayTotal(nfse))}</td>
                                      {(canDelete || canEdit) && (
                                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                          <div className="flex items-center justify-center gap-2">
                                            {canEdit && (
                                              <button
                                                type="button"
                                                title="Editar cabeçalho"
                                                onClick={() => openHeaderEditModal('nfse', nfse)}
                                                className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                              >
                                                <PencilIcon className="h-4 w-4" />
                                              </button>
                                            )}
                                            {canDelete && (
                                              <button type="button" title="Excluir" onClick={() => openDeleteModal('nfse', nfse.id, `NFS-e ${nfse.number || nfse.id}`)} className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors">
                                                <TrashIcon className="h-4 w-4" />
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {renderPagination(nfseNotesPage, nfseNotesPagesCount, filteredNfseNotes.length, setNfseNotesPage)}
                            </>
                          )}
                        </div>
                      </>
                    )}

                    {/* Subaba Itens NFS-e */}
                    {nfseSubTab === 'items' && (
                      <>
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                          <Input type="text" placeholder="Descrição Serviço" value={nfseItemsSearch} onChange={(e) => { setNfseItemsSearch(e.target.value); setNfseItemsPage(1); setNfseNotesPage(1) }} />
                        </div>
                        {renderTotalsSummary([
                          { label: 'Soma Qtd', value: nfseItemsQuantityTotal, kind: 'quantity' },
                          { label: 'Soma Vl. Unit.', value: nfseItemsUnitValueTotal },
                          { label: 'Média Vl. Unit.', value: nfseItemsUnitValueAverage },
                          { label: 'Soma Total', value: nfseItemsTotalValue },
                        ])}
                        {/* Tabela */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                          {paginatedNfseItems.length === 0 ? (
                            <div className="px-4 py-12 text-center">
                              <DocumentIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
                              <Text size="sm" color="muted">{allNfseItems.length === 0 ? 'Nenhum item cadastrado.' : 'Nenhum item encontrado com os filtros aplicados.'}</Text>
                            </div>
                          ) : (
                            <>
                              <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <SortableTableHeader sortKey="number" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3">Nota</SortableTableHeader>
                                    <SortableTableHeader sortKey="provider_name" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3">Prestador</SortableTableHeader>
                                    <SortableTableHeader sortKey="description" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3">Detalhes do Serviço</SortableTableHeader>
                                    <SortableTableHeader sortKey="quantity" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3 text-right">Qtd</SortableTableHeader>
                                    <SortableTableHeader sortKey="unit_value" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3 text-right">Vl. Unit.</SortableTableHeader>
                                    <SortableTableHeader sortKey="total_value" currentSort={nfseItemsSort} sortOrder={nfseItemsSortOrder} onSort={toggleSort(nfseItemsSort, setNfseItemsSort, nfseItemsSortOrder, setNfseItemsSortOrder, setNfseItemsPage)} className="px-4 py-3 text-right">Total</SortableTableHeader>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Centro de Custo</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {paginatedNfseItems.map((item) => {
                                    const header = nfseHeaderMap.get(item.nfse_id)
                                    return (
                                      <tr key={item.id} onClick={() => header && void openNfseDetail(header)} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer">
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap font-medium">{header?.number || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[160px] truncate" title={header?.provider_name ?? ''}>{header?.provider_name || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white max-w-[220px] truncate" title={item.description ?? ''}>{item.description || '—'}</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{item.quantity ?? '—'}</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(item.unit_value)}</td>
                                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(item.total_value)}</td>
                                        <td className="px-4 py-3 min-w-[160px]" onClick={(e) => e.stopPropagation()}>{renderInlineItemEdit(item, 'nfse', 'category', 'list')}</td>
                                        <td className="px-4 py-3 min-w-[160px]" onClick={(e) => e.stopPropagation()}>{renderInlineItemEdit(item, 'nfse', 'cost_center', 'list')}</td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                              {renderPagination(nfseItemsPage, nfseItemsPagesCount, filteredNfseItems.length, setNfseItemsPage)}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </PermissionGuard>
      </main>

      {/* Loading overlay detalhe */}
      {loadingDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <LoadingSpinner />
        </div>
      )}

      {/* Modal Detalhe NF-e */}
      {detailNfe && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                NF-e {detailNfe.header.number || '—'}
                {detailNfe.header.series && (
                  <span className="text-base font-normal text-gray-400 dark:text-gray-500 ml-2">
                    Série {detailNfe.header.series}
                  </span>
                )}
              </Heading>
              <Button variant="ghost" onClick={() => setDetailNfe(null)}>Fechar</Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Cabeçalho */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Dados da Nota
                </Text>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Chave de Acesso</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 break-all font-mono text-xs">{detailNfe.header.access_key || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Data de Emissão</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detailNfe.header.issue_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Nº Itens</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfe.items.length}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Emitente</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfe.header.issuer_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">CNPJ Emitente</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detailNfe.header.issuer_cnpj)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Destinatário</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfe.header.recipient_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">CPF/CNPJ Destinatário</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detailNfe.header.recipient_document)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Meio de Pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfe.header.payment_method || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Arquivo</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">
                      {detailNfe.header.file_name ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1 text-sm">
                            <DocumentIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            {detailNfe.header.file_name}
                          </span>
                          <a
                            href={`${ADM_STORAGE_BASE}${encodeURIComponent(detailNfe.header.file_name)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                            Baixar documento
                          </a>
                        </div>
                      ) : '—'}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Valores */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Valores
                </Text>
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Produtos</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-medium">{formatCurrency(detailNfe.header.total_products)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Frete</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatCurrency(detailNfe.header.total_freight)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Desconto</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatCurrency(detailNfe.header.total_discount)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Total</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-semibold text-base">{formatCurrency(detailNfe.header.total_value)}</dd>
                  </div>
                </dl>
              </div>

              {/* Itens */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Itens ({detailNfe.items.length})
                </Text>
                {detailNfe.items.length === 0 ? (
                  <Text size="sm" color="muted">Nenhum item cadastrado.</Text>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Descrição</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">NCM</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Qtd</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Un</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vl. Unit.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Categoria</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Centro de Custo</th>
                          {(canDelete || canEdit) && <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ações</th>}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {detailNfe.items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{item.description || '—'}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{item.ncm || '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{item.quantity ?? '—'}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{item.unit || '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(item.unit_value)}</td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.total_value)}</td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {item.category_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                  {categoryMap.get(item.category_id) ?? item.category_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {item.cost_center_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                  {costCenterMap.get(item.cost_center_id) ?? item.cost_center_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            {(canDelete || canEdit) && (
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  {canEdit && (
                                    <button
                                      type="button"
                                      title="Editar item"
                                      onClick={() => openItemEditModal('nfe', item)}
                                      className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      title="Excluir item"
                                      onClick={() => void handleSoftDeleteItem('nfe', item)}
                                      disabled={savingItem === item.id}
                                      className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalhe NFS-e */}
      {detailNfse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                NFS-e {detailNfse.header.number || '—'}
              </Heading>
              <Button variant="ghost" onClick={() => setDetailNfse(null)}>Fechar</Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Cabeçalho */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Dados da Nota
                </Text>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Chave de Acesso</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 break-all font-mono text-xs">{detailNfse.header.access_key || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Número RPS</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.rps_number || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Data de Emissão</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDate(detailNfse.header.issue_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Competência</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">
                      {detailNfse.header.competence_date
                        ? new Date(detailNfse.header.competence_date + 'T00:00:00').toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Município</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.municipality || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Prestador</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.provider_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">CNPJ Prestador</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detailNfse.header.provider_cnpj)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Insc. Municipal</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.provider_im || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Tomador</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.customer_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">CPF/CNPJ Tomador</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatDocument(detailNfse.header.customer_document)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Meio de Pagamento</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{detailNfse.header.payment_method || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Arquivo</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">
                      {detailNfse.header.file_name ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1 text-sm">
                            <DocumentIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            {detailNfse.header.file_name}
                          </span>
                          <a
                            href={`${ADM_STORAGE_BASE}${encodeURIComponent(detailNfse.header.file_name)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                            Baixar documento
                          </a>
                        </div>
                      ) : '—'}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Valores */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Valores
                </Text>
                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Total Serviços</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-medium">{formatCurrency(detailNfse.header.total_services)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Desconto</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5">{formatCurrency(detailNfse.header.total_discount)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 font-medium">Valor Líquido</dt>
                    <dd className="text-gray-900 dark:text-white mt-0.5 font-semibold text-base">{formatCurrency(detailNfse.header.net_value)}</dd>
                  </div>
                </dl>
              </div>

              {/* Serviços */}
              <div>
                <Text size="sm" className="font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  Serviços ({detailNfse.items.length})
                </Text>
                {detailNfse.items.length === 0 ? (
                  <Text size="sm" color="muted">Nenhum serviço cadastrado.</Text>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Descrição</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Código</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Qtd</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Vl. Unit.</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Categoria</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Centro de Custo</th>
                          {(canDelete || canEdit) && <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ações</th>}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {detailNfse.items.map((item) => (
                          <tr key={item.id}>
                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{item.description || '—'}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{item.service_code || '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{item.quantity ?? '—'}</td>
                            <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(item.unit_value)}</td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{formatCurrency(item.total_value)}</td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {item.category_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                  {categoryMap.get(item.category_id) ?? item.category_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 min-w-[160px]">
                              {item.cost_center_id ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                  {costCenterMap.get(item.cost_center_id) ?? item.cost_center_id}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                              )}
                            </td>
                            {(canDelete || canEdit) && (
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  {canEdit && (
                                    <button
                                      type="button"
                                      title="Editar item"
                                      onClick={() => openItemEditModal('nfse', item)}
                                      className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button
                                      type="button"
                                      title="Excluir item"
                                      onClick={() => void handleSoftDeleteItem('nfse', item)}
                                      disabled={savingItem === item.id}
                                      className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {itemEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
              Editar item {itemEditModal.tableType.toUpperCase()}
            </Heading>
            <div className="space-y-4">
              <div>
                <Text size="sm" color="muted">Nota: {itemEditModal.headerNumber}</Text>
                <Text size="sm" color="muted" className="truncate">Item: {itemEditModal.description || itemEditModal.itemId}</Text>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Descrição</label>
                  <Input
                    type="text"
                    value={itemEditModal.description}
                    onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, description: e.target.value } : prev)}
                  />
                </div>
                {itemEditModal.tableType === 'nfe' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">NCM</label>
                      <Input
                        type="text"
                        value={itemEditModal.ncm}
                        onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, ncm: e.target.value } : prev)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Unidade</label>
                      <Input
                        type="text"
                        value={itemEditModal.unit}
                        onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, unit: e.target.value } : prev)}
                      />
                    </div>
                  </>
                )}
                {itemEditModal.tableType === 'nfse' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Código do serviço</label>
                    <Input
                      type="text"
                      value={itemEditModal.serviceCode}
                      onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, serviceCode: e.target.value } : prev)}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Quantidade</label>
                  <Input
                    type="number"
                    step="any"
                    value={itemEditModal.quantity}
                    onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, quantity: e.target.value } : prev)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Valor unitário</label>
                  <Input
                    type="number"
                    step="any"
                    value={itemEditModal.unitValue}
                    onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, unitValue: e.target.value } : prev)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Valor total</label>
                  <Input
                    type="number"
                    step="any"
                    value={itemEditModal.totalValue}
                    onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, totalValue: e.target.value } : prev)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Categoria</label>
                <select
                  value={itemEditModal.categoryId}
                  onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, categoryId: e.target.value } : prev)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">— Nenhuma —</option>
                  {categorias.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Centro de custo</label>
                <select
                  value={itemEditModal.costCenterId}
                  onChange={(e) => setItemEditModal((prev) => prev ? { ...prev, costCenterId: e.target.value } : prev)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-2 text-sm text-gray-900 dark:text-white shadow-sm"
                >
                  <option value="">— Nenhum —</option>
                  {centrosCusto.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={closeItemEditModal} disabled={submittingItemEdit}>
                Cancelar
              </Button>
              <Button variant="solid" onClick={() => void handleSaveItemEdit()} disabled={submittingItemEdit}>
                {submittingItemEdit ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de upload / cadastrar despesa */}
      {uploadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">

            {/* Cabeçalho */}
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Cadastrar Despesas
              </Heading>
              <button
                type="button"
                onClick={closeUploadModal}
                disabled={isProcessing}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-40 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Zona de seleção */}
            {!isProcessing && (
              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-5 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors mb-4"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
                <ArrowUpTrayIcon className="mx-auto h-7 w-7 text-gray-400 dark:text-gray-500 mb-1" />
                <Text size="sm" color="muted">
                  Clique para adicionar PDFs
                  {uploadFiles.length > 0 && ' (adicionar mais)'}
                </Text>
              </div>
            )}

            {/* Resumo do progresso quando processando */}
            {(isProcessing || processingDone) && (() => {
              const total = uploadFiles.length
              const done = uploadFiles.filter((f) => f.status === 'success' || f.status === 'error').length
              const success = uploadFiles.filter((f) => f.status === 'success').length
              const errors = uploadFiles.filter((f) => f.status === 'error').length
              const pct = total > 0 ? Math.round((done / total) * 100) : 0
              return (
                <div className="mb-4 space-y-2">
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{done} de {total} arquivo{total !== 1 ? 's' : ''} processado{total !== 1 ? 's' : ''}</span>
                    <span className="font-medium">{pct}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${errors > 0 && done === total ? 'bg-yellow-500' : 'bg-blue-600'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    {processingStartedAt && (
                      <span>Início: <span className="font-mono text-gray-700 dark:text-gray-300">{formatTime(processingStartedAt)}</span></span>
                    )}
                    {processingDone && processingFinishedAt && (
                      <span>Fim: <span className="font-mono text-gray-700 dark:text-gray-300">{formatTime(processingFinishedAt)}</span></span>
                    )}
                    {processingDone && success > 0 && (
                      <span className="text-green-600 dark:text-green-400 font-medium">{success} enviado{success !== 1 ? 's' : ''}</span>
                    )}
                    {processingDone && errors > 0 && (
                      <span className="text-red-600 dark:text-red-400 font-medium">{errors} com erro</span>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* Lista de arquivos */}
            {uploadFiles.length > 0 && (
              <ul className="space-y-2 max-h-64 overflow-y-auto mb-4 pr-1">
                {uploadFiles.map((entry, idx) => (
                  <li
                    key={entry.file.name + idx}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 bg-gray-50 dark:bg-gray-700/50"
                  >
                    {/* Ícone de status */}
                    <div className="flex-shrink-0">
                      {entry.status === 'pending' && (
                        <DocumentIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      )}
                      {entry.status === 'sending' && (
                        <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                      )}
                      {entry.status === 'success' && (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      )}
                      {entry.status === 'error' && (
                        <div className="h-5 w-5 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                          <XMarkIcon className="h-3 w-3 text-red-500" />
                        </div>
                      )}
                    </div>

                    {/* Nome e info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={entry.file.name}>
                        {entry.file.name}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {entry.status === 'pending' && `${(entry.file.size / 1024).toFixed(1)} KB`}
                        {entry.status === 'sending' && 'Enviando...'}
                        {entry.status === 'success' && (
                          <span className="text-green-600 dark:text-green-400">
                            Concluído{entry.finishedAt ? ` às ${formatTime(entry.finishedAt)}` : ''}
                          </span>
                        )}
                        {entry.status === 'error' && (
                          <span className="text-red-500 dark:text-red-400">
                            {entry.error ?? 'Erro ao enviar'}
                            {entry.finishedAt ? ` — ${formatTime(entry.finishedAt)}` : ''}
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Remover (somente antes de processar) */}
                    {!isProcessing && entry.status === 'pending' && (
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="flex-shrink-0 text-gray-300 hover:text-red-400 dark:text-gray-600 dark:hover:text-red-400 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {uploadFiles.length === 0 && !isProcessing && (
              <Text size="sm" color="muted" className="text-center mb-4">
                Nenhum arquivo selecionado.
              </Text>
            )}

            {/* Rodapé */}
            <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                size="md"
                onClick={closeUploadModal}
                disabled={isProcessing}
              >
                {processingDone ? 'Fechar' : 'Cancelar'}
              </Button>
              {!processingDone && (
                <Button
                  variant="solid"
                  size="md"
                  onClick={handleUploadSubmit}
                  disabled={uploadFiles.filter((f) => f.status === 'pending').length === 0 || isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Processando...
                    </>
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      Enviar {uploadFiles.filter((f) => f.status === 'pending').length > 0
                        ? `${uploadFiles.filter((f) => f.status === 'pending').length} arquivo${uploadFiles.filter((f) => f.status === 'pending').length !== 1 ? 's' : ''}`
                        : ''}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {headerEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div>
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Editar Cabeçalho {headerEditModal.type === 'nfe' ? 'NF-e' : 'NFS-e'}
                </Heading>
                <Text size="sm" color="muted" className="mt-1">
                  Atualize os dados principais do cabeçalho da nota.
                </Text>
              </div>
              <Button variant="ghost" onClick={closeHeaderEditModal} disabled={submittingHeaderEdit}>
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número da nota</label>
                  <Input type="text" value={headerEditModal.number} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, number: e.target.value } : prev)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data de emissão</label>
                  <Input type="date" value={headerEditModal.issueDate} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, issueDate: e.target.value } : prev)} />
                </div>
                {headerEditModal.type === 'nfe' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Série</label>
                    <Input type="text" value={headerEditModal.series} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, series: e.target.value } : prev)} />
                  </div>
                )}
                <div className={headerEditModal.type === 'nfe' ? 'md:col-span-2' : 'md:col-span-2'}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{headerEditModal.type === 'nfe' ? 'Emitente' : 'Prestador'}</label>
                  <Input type="text" value={headerEditModal.name} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, name: e.target.value } : prev)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{headerEditModal.type === 'nfe' ? 'CNPJ emitente' : 'CNPJ prestador'}</label>
                  <Input type="text" value={headerEditModal.document} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, document: e.target.value } : prev)} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chave de acesso</label>
                  <Input type="text" value={headerEditModal.accessKey} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, accessKey: e.target.value } : prev)} />
                </div>

                {headerEditModal.type === 'nfe' && (
                  <>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destinatário</label>
                      <Input type="text" value={headerEditModal.recipientName} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, recipientName: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF/CNPJ destinatário</label>
                      <Input type="text" value={headerEditModal.recipientDocument} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, recipientDocument: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total produtos</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalProducts} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalProducts: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Frete</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalFreight} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalFreight: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Desconto</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalDiscount} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalDiscount: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor total</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalValue} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalValue: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meio de pagamento</label>
                      <Input type="text" value={headerEditModal.paymentMethod} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, paymentMethod: e.target.value } : prev)} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome do arquivo</label>
                      <Input type="text" value={headerEditModal.fileName} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, fileName: e.target.value } : prev)} />
                    </div>
                  </>
                )}
                {headerEditModal.type === 'nfse' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">RPS</label>
                      <Input type="text" value={headerEditModal.rpsNumber} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, rpsNumber: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Competência</label>
                      <Input type="date" value={headerEditModal.competenceDate} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, competenceDate: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Inscrição municipal</label>
                      <Input type="text" value={headerEditModal.providerIm} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, providerIm: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Município</label>
                      <Input type="text" value={headerEditModal.municipality} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, municipality: e.target.value } : prev)} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tomador</label>
                      <Input type="text" value={headerEditModal.recipientName} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, recipientName: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF/CNPJ tomador</label>
                      <Input type="text" value={headerEditModal.recipientDocument} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, recipientDocument: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total serviços</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalServices} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalServices: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Desconto</label>
                      <Input type="number" step="0.01" value={headerEditModal.totalDiscount} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, totalDiscount: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor líquido</label>
                      <Input type="number" step="0.01" value={headerEditModal.netValue} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, netValue: e.target.value } : prev)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meio de pagamento</label>
                      <Input type="text" value={headerEditModal.paymentMethod} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, paymentMethod: e.target.value } : prev)} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome do arquivo</label>
                      <Input type="text" value={headerEditModal.fileName} onChange={(e) => setHeaderEditModal((prev) => prev ? { ...prev, fileName: e.target.value } : prev)} />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={closeHeaderEditModal}
                disabled={submittingHeaderEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleSaveHeaderEdit()}
                disabled={submittingHeaderEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingHeaderEdit ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {printDialogView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Imprimir registros
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Selecione o escopo da impressão para a subaba atual.
            </Text>

            <div className="space-y-2">
              <button
                type="button"
                onClick={() => handlePrint('page')}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-left"
              >
                Página atual
              </button>
              <button
                type="button"
                onClick={() => handlePrint('filtered')}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-left"
              >
                Todos os filtrados
              </button>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => setPrintDialogView(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de exclusão lógica */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Confirmar Exclusão
            </Heading>
            <Text size="sm" color="muted" className="mb-4">
              Você está excluindo: <strong className="text-gray-900 dark:text-white">{deleteModal.label}</strong>
              <br />
              O registro não será removido do banco de dados (exclusão lógica).
            </Text>

            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo da exclusão <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Informe o motivo..."
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={submittingDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={submittingDelete || !deleteReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingDelete ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdmDespesas
